import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import {
  Box,
  Typography,
  Icon,
  Link,
  ButtonBase,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';

import useStyles from './styles';
import Loading from './Loading';

const Course = props => {
  const classes = useStyles(props);
  const { id, view, loading } = props;
  const [courseLoading, setCourseLoading] = useState(true);
  const [showDesc, setShowDesc] = useState(false);
  const [lockedText, setLockedText] = useState('');
  const [conclusionPercent, setConclusionPercent] = useState(0);
  const courseDetails = useSelector(state => state.course.list[id]);
  const [courseEmpty, setCourseEmpty] = useState(false);

  const getBtnText = () => {
    if (courseDetails.locked) {
      return 'Em breve';
    }
    if (conclusionPercent === 0 && !courseDetails.locked) {
      return 'Começar';
    }
    if (conclusionPercent === 100) {
      return 'Abrir';
    }
    return 'Continuar';
  };

  const handleDescription = () => {
    setShowDesc(!showDesc);
  };

  const courseLink = React.useMemo(() => {
    if (courseDetails) {
      const { id: courseId, nextLesson, showBlockedLessons } = courseDetails;
      let path = `/course/${courseId}/`;
      if (nextLesson) {
        const {
          id: nextLessonId,
          certificate,
          daysToRelease,
          empty: emptyNextLesson,
          completed,
        } = nextLesson;

        const notShowBlockedLesson = daysToRelease > 0 && !showBlockedLessons;
        const certificateUnlocked =
          certificate ||
          certificate?.daysToRelease <= 0 ||
          certificate?.conclusionPercent <= conclusionPercent;

        if (emptyNextLesson || (notShowBlockedLesson && !certificateUnlocked)) {
          setCourseEmpty(true);
          path = '';
        } else {
          if (courseEmpty) setCourseEmpty(false);
          const certificateIsNext = completed && certificateUnlocked;
          path = `/course/${courseId}/${
            certificateIsNext ? 'certificate' : nextLessonId
          }`;
        }
      }
      return path;
    }
    return '';
  }, [conclusionPercent, courseDetails, courseEmpty]);

  const LinkCourse = useCallback(
    linkProps => {
      const { children, ...otherProps } = linkProps;
      return courseDetails?.locked || courseEmpty ? (
        <Box {...otherProps}>{children}</Box>
      ) : (
        <Link
          component={RouterLink}
          to={courseLink}
          underline="none"
          color="inherit"
          {...otherProps}
        >
          {children}
        </Link>
      );
    },
    [courseDetails, courseEmpty, courseLink],
  );

  const LockIcon = useCallback(
    ({ show, ...iconProps }) => {
      if (courseDetails?.locked && show) {
        return (
          <Tooltip title={lockedText} {...iconProps}>
            <Box className="lock">
              <Icon>lock</Icon>
            </Box>
          </Tooltip>
        );
      }
      if (courseEmpty && show) {
        return (
          <Tooltip
            title="Este curso ainda não possuí conteúdo disponível."
            {...iconProps}
          >
            <Box className="lock">
              <Icon>block</Icon>
            </Box>
          </Tooltip>
        );
      }
      return null;
    },
    [courseDetails, courseEmpty, lockedText],
  );

  useEffect(() => {
    setCourseLoading(false);
    setConclusionPercent(courseDetails?.conclusionPercent || 0);
  }, [courseDetails]);

  useEffect(() => {
    setLockedText(
      courseDetails?.locked
        ? `Este curso será liberado em ${moment(
            courseDetails?.releaseDate,
          ).format('DD/MM/YYYY')}`
        : '',
    );
  }, [courseDetails]);

  useEffect(() => {
    if (loading) {
      setCourseLoading(true);
    }
  }, [loading]);

  return courseLoading ? (
    <Loading view={view} />
  ) : (
    <Box
      className={`${classes.container} ${view} ${
        courseDetails.locked || courseEmpty ? 'locked' : ''
      }`}
    >
      <LinkCourse className="img" id={courseDetails.id}>
        {courseDetails.photo ? (
          <img src={courseDetails.photo} alt="" />
        ) : (
          <Icon className="icon">photo_size_select_actual</Icon>
        )}
      </LinkCourse>
      <LinkCourse className="progress" id={courseDetails.id}>
        {!courseDetails.locked && (
          <Box className="progress-bar">
            <Box
              className="progress-bar-fill"
              width={`${conclusionPercent}%`}
            />
          </Box>
        )}
        {view === 'list' && (
          <>
            {!courseDetails.locked ? (
              <Typography className="progress-text" variant="caption" noWrap>
                <i>
                  {`${conclusionPercent}% `}
                  <span>Concluído</span>
                </i>
              </Typography>
            ) : (
              <Typography className="progress-text" variant="caption" noWrap>
                {lockedText}
              </Typography>
            )}
          </>
        )}
      </LinkCourse>
      <LinkCourse className="content" id={courseDetails.id}>
        <Typography className="title" variant="body2">
          {courseDetails.name}
        </Typography>
        <Typography className="instructor" variant="caption">
          <i>{courseDetails.instructorName}</i>
        </Typography>
        <LockIcon show={view === 'grid'} />
      </LinkCourse>

      {view === 'list' && (
        <>
          <LockIcon placement="right" />
          <LinkCourse
            className={`${showDesc ? 'active' : ''} description`}
            id={courseDetails.id}
          >
            <Typography className="description-text" variant="body2">
              {courseDetails.description}
            </Typography>
          </LinkCourse>
          <Box className="footer-bar">
            <IconButton
              className={`btn-info ${showDesc ? 'active' : ''}`}
              onClick={handleDescription}
              size="small"
            >
              <Icon>info</Icon>
            </IconButton>
            <LockIcon />
            <ButtonBase className="btn">
              <Typography variant="caption">{getBtnText()}</Typography>
              <Icon fontSize="small">chevron_right</Icon>
            </ButtonBase>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Course;
